import { CLIENT_SYSTEM_PROTO } from "./clientSystemMethods.js";
import { Process, System, isNullOrUndefined, BaseAssetManager } from "@gotti/core"
import { getAreaIdsForSystem, resolveAreaForSystem } from "./areaRouting.js";
import type { AssetAction, AssetChange } from "@gotti/core";
import { BaseClientConnector } from "./ClientConnectors/BaseClientConnector.js";
import { Client, ClientAreaFile, ClientGameFile } from "./Client.js";
import { IndexedDBLoader } from './IndexedDbLoader.js';
import { ClientDebugSystem } from './debug/ClientDebugSystem.js';
import { type IClientSystem } from './IClientSystem.js';
import { attachClientAssets } from './assets/inject.js';
import { HttpAssetSource } from './assets/HttpAssetSource.js';
import { makeClientAssetsFacade, type ClientAssetsFacade } from './assets/assetsFacade.js';

/**
 * Client dispatch methods injected by `decorate()` from `ClientSystem.prototype`.
 * Subclasses must NOT provide their own implementations — use `declare` instead.
 */
const CLIENT_DISPATCH_METHODS: readonly string[] = [
  'initialize', 'dispatchToServer', 'instantDispatchToServer', 'dispatchRaw',
  'dispatchToArea', 'requestArea',
  'dispatchToAllPeers', 'dispatchToPeers', 'dispatchToPeer',
  'dispatchToPeerRaw', 'dispatchToPeersRaw',
  'dispatchToAllPeersAndServer', 'instantDispatchToAllPeersAndServer',
  'addPeer', 'removePeer', 'getPeerPing', 'getPeerStatus',
  'isConnectedToPeer', 'handleUpdateState',
];

export type ProcessConfig = {
    name: string,
    systems:  Array<System & IClientSystem>
}

export type StartedAreaData = {
    ids: Array<string | number>
    type: string,
    addedApi: string[],
}

export type JoinedAreaMetaData = {
    id: string | number,
    type: string | number,
}

export type JoinedAreaData = JoinedAreaMetaData & {
    data: any,
    state: any
}

/**
 * Client-side process that runs client systems and manages connectors.
 */
export class ClientProcess extends Process { 
    readonly clients : {[clientId: string]: any } = {};

    public isNetworked: boolean = false;

    declare readonly systemLookup : {[system: string]: System & IClientSystem };
    declare readonly systemArray : Array<System & IClientSystem>;

    readonly startedAreas: {[areaType: string]: StartedAreaData } = {};
    readonly joinedAreasById : {[areaId: string]: JoinedAreaMetaData } = {}
    readonly $areaData : {[areaId: string]: any } = {}
    readonly $areaState : {[areaId: string]: any } = {}

    public readonly rawAreaMessageSystems: (System & IClientSystem)[] = [];
    public readonly rawPeerMessageSystems: (System & IClientSystem)[] = [];

    // Lazily built on the first system add (see onAddedSystem) and shared by
    // every system on this process. TODO(Task 9): derive the base URL from
    // the real gate/connector config once the http-serving route lands;
    // '/assets' assumes the client is served same-origin as that route.
    // Constructing these never touches the network, so this can't crash a
    // process whose systems never read an asset.
    private assetManager?: BaseAssetManager;
    private assetsFacade?: ClientAssetsFacade;
    private readonly assetUnsubscribers = new WeakMap<System, () => void>();

    private lastFrameTimeMs: number = 0;
    private tickLengthMs : number = 0;
    private __requestAnimationFrame : any;
    // Resolved in onStartLoop TOGETHER with __requestAnimationFrame — never at
    // module load. A canceller captured at import time is a different timer
    // source from any rAF/setTimeout installed afterwards (fake timers, a
    // polyfilled rAF, the setTimeout fallback), so stopLoop() would "cancel"
    // an id it never issued and the loop would keep running.
    private __cancelAnimationFrame : (id: any) => void;
    private startedGameFile : ClientGameFile;

    private delta : number = 0;
    private activeLoop: any;
    /**
     * Which run of the loop is live. `onStopLoop` moves it on, and every scheduled frame carries the run it belongs
     * to, so a stopped run — stopped from outside, from inside its own frame (a system's `onUpdate` calling
     * `stopLoop`), or before a restart — never updates or schedules another frame (as ServerProcess's loop).
     */
    private loopRun: number = 0;
    private tick : number = 0;
    private connector : BaseClientConnector;
    private fixedTimeStep: boolean = false;

  //  readonly client : Client;

    public started : boolean = false;

    private starting : boolean = false;
   
    readonly indexedDb?: IndexedDBLoader;

    constructor(readonly client: Client, indexedDb?: IndexedDBLoader) {
      super();
      this.indexedDb = indexedDb;
      this.gameLoop = this.gameLoop.bind(this);
    }

    public decorate(system: System): System & IClientSystem {
      // Already decorated — idempotent return
      if ('__is_client' in system) return system as unknown as System & IClientSystem;

      // ── Runtime guard: detect accidental dispatch overrides ─────────
      // All dispatch methods are injected at runtime. If a user writes
      // a method body, the method will be a real function on the prototype
      // → throw to prevent silent breakage.
      for (const name of CLIENT_DISPATCH_METHODS) {
        if (typeof (system as any)[name] === 'function') {
          const systemName = (system as any).name ?? '(unknown)';
          throw new Error(
            `Client system "${systemName}" defines its own "${name}" method. ` +
            `This method is injected at runtime by ClientProcess and must not be overridden. ` +
            `Remove the method body and use \`declare ${name}: IClientSystem['${name}']\` instead.`
          );
        }
      }

      // Inject client-system prototype methods onto the system instance.
      for (const key of Object.getOwnPropertyNames(CLIENT_SYSTEM_PROTO)) {
        if (key in system) continue; // preserve existing properties
        const desc = Object.getOwnPropertyDescriptor(CLIENT_SYSTEM_PROTO, key);
        if (desc) Object.defineProperty(system, key, desc);
      }

      // Add required instance state that doesn't exist yet
      if (!('__is_client' in system)) {
        Object.defineProperty(system, '__is_client', { value: true, writable: false, configurable: false, enumerable: false });
      }
      if (!('requestCbs' in system)) {
        Object.defineProperty(system, 'requestCbs', { value: {}, writable: true, configurable: true, enumerable: true });
      }
      if (!('states' in system)) {
        Object.defineProperty(system, 'states', { value: {}, writable: true, configurable: true, enumerable: true });
      }

      return system as unknown as System & IClientSystem;
    }

    public setConnector(connector : BaseClientConnector) {
        this.connector = connector;
    }

    public ensureAssetManager(): BaseAssetManager {
      if (!this.assetManager) this.assetManager = new BaseAssetManager(new HttpAssetSource('/assets'));
      return this.assetManager;
    }

    /**
     * The `$assets` facade handed to game/area `api` factories (via
     * `envConfigOptions.$assets`) so they can read preloaded assets
     * synchronously. Built lazily over the one shared asset manager and
     * memoized — one facade per process, not one per call.
     */
    public getAssetsFacade(): ClientAssetsFacade {
      if (!this.assetsFacade) this.assetsFacade = makeClientAssetsFacade(this.ensureAssetManager());
      return this.assetsFacade;
    }

    /**
     * Guards the window where a game/area file's systems have NOT yet been
     * registered because `startGame`/`initAreaFile` is still awaiting its
     * asset preloads / api file. A server can legally dispatch to a client
     * system the moment `onClientJoined` fires (e.g. "here is the player
     * list" routed to a sync system), which lands in that await window —
     * without buffering, the message is silently dropped and the client
     * never learns its initial state (players invisible while the
     * authoritative server state stays correct).
     */
    // Lazily initialized (`??=`) so borrowed-prototype instances (tests use
    // Object.create(ClientProcess.prototype)) work without the constructor.
    private _systemInitsInFlight?: number;
    private _pendingSystemDeliveries?: Map<string | number, Array<(sys: System) => void>>;

    /**
     * Deliver `deliver(system)` to the system registered under `systemName`.
     * - Registered → delivered immediately (returns true).
     * - Not registered but a game/area file init is in flight → buffered and
     *   flushed (in arrival order) as soon as the init finishes registering
     *   its systems (returns true).
     * - Not registered and nothing initializing → returns false; the caller
     *   decides whether to warn/drop (a genuinely unknown system id).
     */
    public deliverToSystem(systemName: string | number, deliver: (sys: System) => void): boolean {
      const sys = this.systemLookup[systemName];
      if (sys) { deliver(sys); return true; }
      if ((this._systemInitsInFlight ?? 0) > 0) {
        const pending = (this._pendingSystemDeliveries ??= new Map());
        const q = pending.get(systemName);
        if (q) { q.push(deliver); } else { pending.set(systemName, [deliver]); }
        return true;
      }
      return false;
    }

    /** Flush buffered deliveries whose systems are now registered; once no
     *  init remains in flight, warn-drop the rest (their system never came). */
    private _flushPendingSystemDeliveries(): void {
      if (!this._pendingSystemDeliveries?.size) return;
      for (const [name, queue] of Array.from(this._pendingSystemDeliveries.entries())) {
        const sys = this.systemLookup[name];
        if (sys) {
          this._pendingSystemDeliveries.delete(name);
          queue.forEach(deliver => deliver(sys));
        } else if ((this._systemInitsInFlight ?? 0) === 0) {
          this._pendingSystemDeliveries.delete(name);
          console.warn(`[ClientProcess] dropping ${queue.length} buffered message(s) for system ${String(name)} — it never registered`);
        }
      }
    }

    /**
     * Load each client-namespace asset path into the shared manager's cache
     * BEFORE a game/area file's systems are added, so `onInit`/render can use
     * the synchronous `$assets.client.getJSON/getImage(...)` getters without
     * awaiting. No-op when `paths` is empty/undefined (asset-less games are
     * unaffected). Fails loud — a preload that can't load throws immediately
     * rather than leaving systems to discover a silently-missing asset later.
     */
    public async preloadAssets(paths?: string[]): Promise<void> {
      if (!paths?.length) return;
      const mgr = this.ensureAssetManager();
      await Promise.all(paths.map(async (p) => {
        try { await mgr.loadRaw(`client/${p}`); }
        catch (e) { throw new Error(`Asset preload failed for "client/${p}": ${e instanceof Error ? e.message : String(e)}`); }
      }));
    }

    public async startGame(gameFile: ClientGameFile, gameData?: any) {
        this.startedGameFile = gameFile
        if(this.startedGameFile?.onBeforeUpdate) {
            this.onBeforeUpdate((delta) => this.startedGameFile.onBeforeUpdate(delta));
        } else {
            this.offBeforeUpdate();
        }

        if(this.startedGameFile?.onAfterUpdate) {
            this.onAfterUpdate((delta) => this.startedGameFile.onAfterUpdate(delta));
        } else {
            this.offAfterUpdate();
        }

        this.isNetworked = gameFile.isNetworked;
        this.setGameData(gameData);
        if(this.started || this.starting) throw new Error(`Already started game...`);
        this.starting = true;
        
        // Enable debug system if requested in game file
        if (gameFile.debug) {
            this.enableDebugSystem(ClientDebugSystem);
        }
        
        // Buffer to-system messages that arrive while we're awaiting below —
        // systems only register at addSystemsArrayFromFile, and a server may
        // dispatch to them the moment the client joins.
        this._systemInitsInFlight = (this._systemInitsInFlight ?? 0) + 1;
        try {
            await this.preloadAssets(gameFile.preloads);

            if(gameFile.api) {
                await this.addApiFromFile(gameFile.api, gameData, { ...this.client.envOptions, $assets: this.getAssetsFacade() });
            }

            // console.log('GAME FILE:', gameFile.systems);
            this.addSystemsArrayFromFile(gameFile.systems);
        } finally {
            this._systemInitsInFlight = (this._systemInitsInFlight ?? 1) - 1;
            this._flushPendingSystemDeliveries();
        }
        // Determine timestep mode (client)
        this.fixedTimeStep = !!gameFile.fixedTimeStep;
        const stepSec = (typeof gameFile.timeStep === 'number') ? gameFile.timeStep : (1 / 60);
        this.startLoop(stepSec * 1000);
        this.started = true;
        this.starting = false;
    }

    public denitAreaFile(areaFile: ClientAreaFile, areaId?: string | number) : StartedAreaData  {
        let area = null;
        if(areaFile) {
            area = this.startedAreas[areaFile.type];
            if(!(area)) {
                throw new Error(`No area type found for type: ${areaFile.type}`);
            }
        }
       
        const hasId = !isNullOrUndefined(areaId)
        if(hasId) {
            const idx = area.ids.indexOf(areaId);
            if(idx < -1) {
                throw new Error(`No area with id: ${areaId} existed`);
            }
            this.removeAreaData(areaId);
            area.ids.splice(idx, 1);
        } else if(area.ids.length) {
            throw new Error(`Stopping area with area id: ${areaId} but no areas with an id of type: ${areaFile.type} had an id.`)
        }

        if(!area.ids.length) {
            this.removeSystemsArrayFromFile(areaFile.systems || [], areaFile.type);
            this.removeApis(area.addedApi);
            delete this.startedAreas[areaFile.type];
        //    this.refreshAreaFileUpdates();
        }
        return area;
    }

    public removeAreaData(areaId: string | number) {
			delete this.joinedAreasById[areaId];
			delete this.$areaData[areaId];
			delete this.$areaState[areaId];
			// The leave landed, so the departing mark has nothing left to hide.
			// Both leave paths funnel through here — including the one deferred
			// behind an in-flight initAreaFile — so this is the one place that
			// needs to clear it.
			this.departingAreaIds.delete(String(areaId));
    }

    public async initAreaFile(areaFile: ClientAreaFile, areaId?: string | number, joinedAreaData?: JoinedAreaData) : Promise<StartedAreaData> {
        const alreadyExisted = areaFile.type in this.startedAreas;
        const startedArea : StartedAreaData = alreadyExisted ? this.startedAreas[areaFile.type] : {
            type: areaFile.type,
            ids: [],
            addedApi: [],
        }

        const hasId = areaId !== null && areaId !== undefined;

        if(alreadyExisted) {
            if(!hasId) {
                throw new Error(`Already started area type: ${areaFile.type}, any additional areas of this type must have an ID`);
            } else if(startedArea.ids.includes(areaId)) {
                throw new Error(`Already started area type: ${areaFile.type} with id: ${areaId}`);
            } else if (startedArea.ids.length === 0) {
                throw new Error(`Provided id but the started area of type: ${areaFile.type} was started initially without an id. Make sure all areas are started with an ID if you intend on using this.`)
            }
            startedArea.ids.push(areaId);
            // A SECOND (third, …) instance of an already-started area type: its
            // systems are already registered, but this instance is still a
            // distinct joined area and must appear in `joinedAreasById` like any
            // other — it is what reconnect replays, what `$areaData` exposes, and
            // what makes this system's dispatch target ambiguous (see areaRouting).
            if(joinedAreaData) {
                this.initAreaData(areaId, joinedAreaData);
            }
            return startedArea;
        } else {
            this.startedAreas[areaFile.type] = startedArea;
        }
    
        if(hasId) {
            if(joinedAreaData) {
               this.initAreaData(areaId, joinedAreaData);
            }
            startedArea.ids.push(areaId);
        }

        // Buffer to-system messages that arrive while we're awaiting below —
        // the server dispatches to client systems as soon as onClientJoined
        // fires (e.g. the player-list to a sync system), which is legal in
        // production and must not race this init's awaits (see deliverToSystem).
        this._systemInitsInFlight = (this._systemInitsInFlight ?? 0) + 1;
        try {
            await this.preloadAssets(areaFile.preloads);
            if(areaFile.api) {
                await this.addApiFromFile(areaFile.api, this.$gameData, { ...this.client.envOptions, $assets: this.getAssetsFacade() });
            }
            // Owner = the area TYPE: an area file registers its systems exactly
            // once no matter how many instances of the type are joined, so the
            // type is what identifies "the area that started this system".
            this.addSystemsArrayFromFile(areaFile.systems, areaFile.type);
        } finally {
            this._systemInitsInFlight = (this._systemInitsInFlight ?? 1) - 1;
            this._flushPendingSystemDeliveries();
        }
        return startedArea;
    }

    /** @see getAreaIdsForSystem in ./areaRouting */
    public getAreaIdsForSystem(systemName: string | number): string[] {
      return getAreaIdsForSystem(this, systemName);
    }

    /**
     * The single area a system's un-addressed server send routes to — the area
     * file that started it. Throws when that is ambiguous, or when no area is
     * joined at all. @see ./areaRouting
     */
    public resolveAreaForSystem(systemName: string | number): string | undefined {
      return resolveAreaForSystem(this, systemName);
    }

    /**
     * Areas this client is in the middle of LEAVING, excluded from dispatch
     * routing while they drain (see `getAreaIdsForSystem`).
     *
     * An area switch (`leavesOtherAreas`) joins the incoming area BEFORE
     * leaving the outgoing ones — deliberately, so a system present in both
     * never drops to ref-count 0 and get destroyed mid-switch. The cost is a
     * window where both areas are in `joinedAreasById` and every send from a
     * system they share, or from any game-file system, looks ambiguous. Marking
     * the outgoing areas closes that window without giving up the ref-count
     * continuity.
     */
    readonly departingAreaIds = new Set<string>();

    /** Mark areas as leaving; they stop being routing candidates immediately. */
    public markAreasDeparting(areaIds: Array<string | number>) {
      areaIds.forEach(id => this.departingAreaIds.add(String(id)));
    }

    public initAreaData(areaId: string | number, joinedAreaData: JoinedAreaData) {
        const { data, state, ...metaJoinData } = joinedAreaData;
        this.joinedAreasById[areaId] = metaJoinData;
        this.$areaData[areaId] = data;
        this.$areaState[areaId] = state;
    }

    /**
     * Idempotent area rebind after a reconnect. The area file + its systems are
     * still live from the original join (membership never lapsed), so we ONLY
     * refresh the cached area data/state — we deliberately do NOT re-run
     * `initAreaFile` (that would double-register systems) and leave the existing
     * `joinedAreasById` membership entry untouched. The authoritative
     * `AREA_STATE_SYNC` that follows the rebind repopulates live state.
     */
    public rejoinArea(areaId: string | number, areaData: any, areaState: any) {
        this.$areaData[areaId] = areaData;
        this.$areaState[areaId] = areaState;
    }

    public stopGame() {
      if(!this.started) throw new Error(`Game was not started`);
      this.started = false;
      this.stopLoop();
      this.systemArray.forEach(s => {
        s.onStop();
      })
    }

    public clearGame() {
			this.stopGame();
    }

    public initialize(connector : BaseClientConnector) {
      this.connector = connector;
      // On a RECONNECT the process's live systems are NOT re-created (membership
      // never lapsed) — but each system captured the OLD connector reference at
      // first-join time (clientSystemMethods.initialize: `this.connector = ...`),
      // and every client->server send reads that per-system `this.connector`
      // (dispatchToServer / dispatchRaw / dispatchToArea / all peer sends). After
      // a connector-loss rebind that reference points at the DEAD socket, so the
      // whole uplink silently dies while the downlink (driven by the NEW
      // connector calling into the process) keeps working. Re-point every live
      // system at the new connector. On a FRESH join this loop is a no-op —
      // systems are added AFTER initialize — so it only ever matters on rejoin.
      for (let i = 0; i < this.systemArray.length; i++) {
        this.systemArray[i].connector = connector;
      }
    }

    protected get peers() : (string | number)[] {
      return this.connector.connectedPeerIds
    }
    
    public onAddedSystem(system: System & IClientSystem) {
      if(!this.connector && this.isNetworked) throw new Error('Can not add systems until client connector is added to process.');

      // `$areaData` is defined once per INSTANCE (non-configurable): a rolled-back
      // hot reload puts the previous instance back through `_addSystem`.
      if (Object.getOwnPropertyDescriptor(system, '$areaData')?.configurable !== false) {
        Object.defineProperty(system, '$areaData', {
            get: () => this.$areaData,
            set: () => { throw new Error(`Can not set $areaData on system...`); }
        });
      }

      // What `releaseSystem` takes away again — re-attached on every add.
      this.assetUnsubscribers.get(system)?.();
      this.assetUnsubscribers.set(system, attachClientAssets(system, this.ensureAssetManager()));

            system.initialize(this.client, this.connector, this.indexedDb);
            system.isNetworked = this.isNetworked;
            if (typeof system.onRawAreaMessage === 'function' && !this.rawAreaMessageSystems.includes(system)) {
                this.rawAreaMessageSystems.push(system);
            }
            if (typeof system.onRawPeerMessage === 'function' && !this.rawPeerMessageSystems.includes(system)) {
                this.rawPeerMessageSystems.push(system);
            }
    }

    /**
     * A client-visible asset changed cluster-wide (pushed by a connector). Drill to
     * the client namespace only — the client manager tracks `client/` assets — and
     * hand it to the shared per-process manager (internal cache work, then hooks).
     */
    public applyAssetChange(action: AssetAction, change: AssetChange): void {
      if (change?.namespace !== 'client') return;
      this.assetManager?.applyRemoteChange(action, change);
    }

    /**
     * A system instance left this process for good (removed, replaced by a hot
     * reload, or a failed replacement rolled back): drop its asset-change
     * subscription and its raw area/peer message slots.
     */
    protected override releaseSystem(system: System) {
        this.assetUnsubscribers.get(system)?.();
        this.assetUnsubscribers.delete(system);
        for (const list of [this.rawAreaMessageSystems, this.rawPeerMessageSystems]) {
            const idx = list.indexOf(system as System & IClientSystem);
            if (idx > -1) list.splice(idx, 1);
        }
    }

    public reinitSystm(systemName: string | number, systemCode: string) {
        const index = this.systemArray.findIndex(i => i.name === systemName);
        if (index > -1) {
            this.systemArray.splice(index, 1);
        }
    }

    /**
     * The step, in seconds, this process's own loop updates by when its game runs fixed-step
     * (`fixedTimeStep: true` on the game file): one `update(fixedStepSec)` per whole step. `null` when the
     * loop is variable-step or no game has started.
     */
    public get fixedStepSec(): number | null {
        return this.fixedTimeStep ? this.tick : null;
    }

    /** Schedules the next frame of loop run `run`. */
    private scheduleFrame(run: number) {
        this.activeLoop = this.__requestAnimationFrame(() => this.gameLoop(run));
    }

    private gameLoop(run: number = this.loopRun) {
        // A frame of a run that stopLoop() (or a restart) has ended since: it must not run.
        if (run !== this.loopRun) return;
        const currentTime = Date.now();
        if (currentTime < this.lastFrameTimeMs + this.tickLengthMs) {
            this.scheduleFrame(run);
            return;
        }
        const frameDelta = currentTime - this.lastFrameTimeMs;
        this.lastFrameTimeMs = currentTime;
        try {
            if (this.fixedTimeStep) {
                // Accumulate and step at fixed tickLengthMs
                this.delta += frameDelta;
                let numUpdateSteps = 0;
                while (this.delta >= this.tickLengthMs) {
                    this.update(this.tick);
                    this.delta -= this.tickLengthMs;
                    if (++numUpdateSteps >= 240) {
                        this.delta = 0;
                        break;
                    }
                }
            } else {
                // Variable timestep, pass seconds for this frame
                this.update(frameDelta / 1000);
            }
        } catch (err) {
            // A throw escaping update() would skip the reschedule below and kill
            // this client's loop permanently and silently (frozen player, dead
            // input polling) — one bad frame from one system must not do that.
            console.error('[gotti] error in system update — game loop continues', err);
            // Also surface it as a genuine uncaught error (visible in devtools,
            // fires window.onerror/pageerror) on a fresh stack so this catch's
            // reschedule below still runs. Real-Node-only guard: NodeRunnerAdapter
            // and vitest both run ClientProcess under actual Node with a fully
            // browser-shaped `window`/`process` polyfill (test-utils/mocks/nodejs),
            // where an uncaught exception in a setTimeout callback crashes the
            // process by default — the exact failure mode this catch exists to
            // prevent. That polyfill fakes `process.versions.node` on purpose (so
            // other Node-detection checks still pass) but never fakes
            // `process.release` — a real Node process always sets `release.name`,
            // a real browser tab never has a `process.release` at all — so this is
            // the one reliable signal left to tell them apart. This matters because
            // some patterns (e.g. an unguarded sync `$assets` read) deliberately
            // throw every tick to steer a developer toward a fix; without this
            // they'd be reduced to silent, easy-to-miss console.error spam instead
            // of a loud signal.
            const isRealNode = typeof process !== 'undefined' && (process as any).release?.name === 'node';
            if (!isRealNode) setTimeout(() => { throw err; });
        }
        // A system stopped the loop (or restarted it) during this frame: this run is over.
        if (run !== this.loopRun) return;
        this.scheduleFrame(run);
    }

    public onStartLoop(tickLengthMs = 1000/30) {
        this.tickLengthMs = tickLengthMs;

        // Scheduler and canceller are chosen as ONE pair from the timer source
        // live right now, so stopLoop() always cancels with the source that
        // issued the id. rAF is used only when its canceller exists too; an
        // environment with rAF but no cancelAnimationFrame could never stop
        // the loop, so it takes the setTimeout pair instead.
        if (typeof window.requestAnimationFrame === 'function' && typeof window.cancelAnimationFrame === 'function') {
            this.__requestAnimationFrame = window.requestAnimationFrame.bind(window);
            this.__cancelAnimationFrame = window.cancelAnimationFrame.bind(window);
        } else {
            const schedule = setTimeout;
            const cancel = clearTimeout;
            this.__cancelAnimationFrame = (id) => cancel(id);
            this.__requestAnimationFrame = (function(){
                // eslint-disable-next-line no-var
                var lastTimestamp = Date.now(),
                    now,
                    timeout;
                const minTick = Math.min(16, tickLengthMs);
                return function(callback) {
                    now = Date.now();
                    timeout = Math.max(0, minTick - (now - lastTimestamp));
                    lastTimestamp = now + timeout;
                    return schedule(function() {
                        callback(lastTimestamp);
                    }, timeout);
                };
            })();
        }

        this.delta = 0;
        this.lastFrameTimeMs = Date.now();
        this.tick = this.tickLengthMs / 1000;
        this.scheduleFrame(this.loopRun);
    }

    /** Ends the running loop: its scheduled frame is cancelled, and a frame still running schedules no other (see `loopRun`). */
    public onStopLoop() {
        this.loopRun += 1;
        // Never started (stopGame/clear before any startLoop) — nothing to cancel.
        if (this.__cancelAnimationFrame) this.__cancelAnimationFrame(this.activeLoop);
        this.activeLoop = null;
    }
}
